// 📄 casoPruebaService.ts
// "Casos de Prueba" (ver CasosPruebaSection.tsx, ítem "Casos de Prueba" en
// Funcionalidades): escenarios de prueba funcional documentados por
// solución/canal (ej. Commerce → Orden de Compra → "Creación de orden de
// compra"), con datos de prueba en pares nombre/valor libres (el usuario no
// sabe de antemano qué campos va a necesitar cada escenario, a diferencia
// de los checklists técnicos de integración que sí tienen campos fijos).
// Igual patrón de permisos que badisZ/transaccionesZ/programasZ: el rol
// "cliente" solo lee, nunca crea/edita (equipo consultor + owner/admin de
// organización).
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  getDocs,
  serverTimestamp,
  deleteField
} from "firebase/firestore";
import { db } from '../firebase';
import { uploadAttachments, UploadedAttachment } from './storageService.ts';
import { queueEmail } from './mailService.ts';
import { notifySlackDeProyecto } from './slackService.ts';
import { ProjectMember } from './projectService.ts';

export type CasoPruebaEstado = 'Pendiente' | 'Aprobado' | 'Fallido';
export type CasoPruebaTipoError = 'Integración' | 'Datos' | 'Proceso' | 'Otro';

export const CASO_PRUEBA_ESTADOS: CasoPruebaEstado[] = ['Pendiente', 'Aprobado', 'Fallido'];
export const CASO_PRUEBA_TIPOS_ERROR: CasoPruebaTipoError[] = ['Integración', 'Datos', 'Proceso', 'Otro'];

export interface CampoPrueba {
  nombre: string;
  valor: string;
}

// Se piden al mover el estado a Aprobado/Fallido (ver
// CasoPruebaResultadoModal.tsx) — nunca al crear el caso, porque recién
// creado todavía no se ejecutó ningún escenario.
export interface DetalleAprobacion {
  resultado: string;
  consideraciones?: string;
}

export interface DetalleFallo {
  tipoError: CasoPruebaTipoError;
  descripcion: string;
}

export interface CasoPruebaFormData {
  nombre: string;
  solucionRelacionada: string;
  // Ambos opcionales: no todas las soluciones tienen un catálogo de
  // canales definido (ver integrationChannels.ts / CANALES_COMMERCE_ITEMS)
  // — cuando no lo tienen, el caso queda anclado solo a la solución.
  canalId?: string;
  canalNombre?: string;
  campos: CampoPrueba[];
  detalles: string;
}

export interface CasoPrueba extends CasoPruebaFormData {
  id: string;
  estado: CasoPruebaEstado;
  creadoPorUid: string;
  creadoPorNombre: string;
  fechaCreacion?: any;
  ejecutadoPorUid?: string;
  ejecutadoPorNombre?: string;
  fechaEjecucion?: any;
  detalleAprobacion?: DetalleAprobacion;
  detalleFallo?: DetalleFallo;
  // Incidente creado a partir de este caso (ver botón "Crear incidente" en
  // CasoPruebaCard.tsx, solo disponible con estado "Fallido") — vive en la
  // misma colección `projects/{projectId}/incidentes`, no hace falta guardar
  // el projectId acá también.
  incidenteId?: string;
  adjuntos: UploadedAttachment[];
}

const escapeHtml = (str: string): string =>
  str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);

const casosPruebaRef = (projectId: string) => collection(db, "projects", projectId, "casosPrueba");
const casoPruebaRef = (projectId: string, casoId: string) => doc(db, "projects", projectId, "casosPrueba", casoId);

export const getCasosPrueba = async (projectId: string): Promise<CasoPrueba[]> => {
  const snap = await getDocs(casosPruebaRef(projectId));
  return snap.docs
    .map(d => ({ id: d.id, ...(d.data() as Omit<CasoPrueba, 'id'>) }))
    .sort((a, b) => (b.fechaCreacion?.toMillis?.() ?? 0) - (a.fechaCreacion?.toMillis?.() ?? 0));
};

export const createCasoPrueba = async (
  projectId: string,
  data: CasoPruebaFormData,
  files: File[],
  creadoPorUid: string,
  creadoPorNombre: string
): Promise<string> => {
  if (!data.nombre.trim()) throw new Error("El nombre del escenario es obligatorio.");

  const ref = doc(casosPruebaRef(projectId));
  const adjuntos = files.length > 0
    ? await uploadAttachments(`projects/${projectId}/casosPrueba/${ref.id}`, files)
    : [];

  const payload: Record<string, any> = {
    nombre: data.nombre.trim(),
    solucionRelacionada: data.solucionRelacionada,
    campos: data.campos.filter(c => c.nombre.trim() && c.valor.trim()),
    detalles: data.detalles.trim(),
    estado: 'Pendiente' as CasoPruebaEstado,
    creadoPorUid,
    creadoPorNombre,
    fechaCreacion: serverTimestamp(),
    adjuntos
  };
  if (data.canalId) {
    payload.canalId = data.canalId;
    payload.canalNombre = data.canalNombre || '';
  }

  await setDoc(ref, payload);
  return ref.id;
};

export interface CasoPruebaDatosEditables {
  nombre: string;
  campos: CampoPrueba[];
  detalles: string;
  // Adjuntos que ya tenía el caso y que el usuario decidió CONSERVAR (los
  // que borró desde el formulario ya no vienen acá) — se combinan con los
  // recién subidos. Ver CasoPruebaFormModal.tsx.
  adjuntosExistentes: UploadedAttachment[];
}

export const updateCasoPruebaDatos = async (
  projectId: string,
  casoId: string,
  datos: CasoPruebaDatosEditables,
  nuevosArchivos: File[]
) => {
  if (!datos.nombre.trim()) throw new Error("El nombre del escenario es obligatorio.");
  const nuevosAdjuntos = nuevosArchivos.length > 0
    ? await uploadAttachments(`projects/${projectId}/casosPrueba/${casoId}`, nuevosArchivos)
    : [];
  await updateDoc(casoPruebaRef(projectId, casoId), {
    nombre: datos.nombre.trim(),
    campos: datos.campos.filter(c => c.nombre.trim() && c.valor.trim()),
    detalles: datos.detalles.trim(),
    adjuntos: [...datos.adjuntosExistentes, ...nuevosAdjuntos]
  });
};

/**
 * Notifica por correo a los PM del proyecto cuando un caso de prueba se
 * marca Fallido — mismo criterio de destinatarios que notifyNuevoIncidente
 * en incidenteService.ts (los PM son quienes necesitan enterarse de un
 * bloqueo, no todo el equipo). No corta el cambio de estado si el envío
 * falla, solo lo deja loggeado.
 */
const notifyCasoPruebaFallido = async (
  casoNombre: string,
  detalle: DetalleFallo,
  ejecutadoPorNombre: string,
  miembros: ProjectMember[]
): Promise<void> => {
  const emails = miembros.filter(m => m.rol === 'PM').map(m => m.email);
  if (emails.length === 0) return;

  await queueEmail({
    to: emails,
    subject: `[Caso de Prueba] Fallido — ${casoNombre}`,
    html: `
      <p>Un caso de prueba se marcó como <strong>Fallido</strong>.</p>
      <p><strong>Escenario:</strong> ${escapeHtml(casoNombre)}</p>
      <p><strong>Tipo de error:</strong> ${detalle.tipoError}</p>
      <p><strong>Ejecutado por:</strong> ${escapeHtml(ejecutadoPorNombre)}</p>
      <p><strong>Qué falló:</strong></p>
      <p style="white-space:pre-wrap">${escapeHtml(detalle.descripcion)}</p>
    `
  });
};

/**
 * `ejecutadoPor*` se completa siempre que el estado deja de ser
 * "Pendiente" — a diferencia de Incidentes (que solo setea la fecha una
 * vez), acá se sobreescribe en cada cambio: interesa saber quién hizo la
 * ÚLTIMA ejecución, no la primera. `detalle` es obligatorio en la práctica
 * para Aprobado/Fallido (lo exige el modal de UI, ver
 * CasoPruebaResultadoModal.tsx) pero queda opcional acá para que "volver a
 * Pendiente" siga siendo un cambio directo sin detalle. Al aprobar se borra
 * un `detalleFallo` viejo (y viceversa) para que la tarjeta nunca muestre
 * el detalle de un estado que ya no aplica. `casoNombre`/`miembros` solo se
 * usan para armar el email cuando `estado === 'Fallido'`.
 */
export const updateCasoPruebaEstado = async (
  projectId: string,
  casoId: string,
  casoNombre: string,
  estado: CasoPruebaEstado,
  ejecutadoPorUid: string,
  ejecutadoPorNombre: string,
  miembros: ProjectMember[],
  detalle?: DetalleAprobacion | DetalleFallo
) => {
  const payload: Record<string, any> = { estado };
  if (estado !== 'Pendiente') {
    payload.ejecutadoPorUid = ejecutadoPorUid;
    payload.ejecutadoPorNombre = ejecutadoPorNombre;
    payload.fechaEjecucion = serverTimestamp();
  }
  if (estado === 'Aprobado') {
    payload.detalleAprobacion = detalle || deleteField();
    payload.detalleFallo = deleteField();
  } else if (estado === 'Fallido') {
    payload.detalleFallo = detalle || deleteField();
    payload.detalleAprobacion = deleteField();
  }
  await updateDoc(casoPruebaRef(projectId, casoId), payload);

  if (estado === 'Fallido' && detalle) {
    await notifyCasoPruebaFallido(casoNombre, detalle as DetalleFallo, ejecutadoPorNombre, miembros)
      .catch(err => console.error('Error notificando caso de prueba fallido:', err));
    // "Notificaciones más ricas" (2026-08-20, Fase 3) — mismo evento, canal extra.
    notifySlackDeProyecto(projectId, `❌ Caso de prueba *Fallido*: ${casoNombre} (ejecutado por ${ejecutadoPorNombre})`)
      .catch(err => console.error('Error notificando caso de prueba fallido a Slack:', err));
  }
};

/** Se llama justo después de crear el incidente desde CasoPruebaResultadoModal/CasoPruebaCard. */
export const linkIncidenteACaso = async (projectId: string, casoId: string, incidenteId: string) => {
  await updateDoc(casoPruebaRef(projectId, casoId), { incidenteId });
};
